import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { Base64 } from 'js-base64';
import { getImageFormat } from '../../shared/utils/getImageFormat';
import { generatePath } from 'react-router-dom';
import axiosInstance from '../../interceptor/axiosInstance';
import Notification from '../../shared/components/Notification';
import { NotificationTypes } from '../../enums/notificationTypes';
import { calculateClassBreaks, pickColorsForBreaks } from '../../shared/utils/quantitySymbologyHelpers';
import MapPortalService from '../MapPortal/mapportal.service';
import { generateColorMapEntries, HEATMAP_PIXELS_PER_CELL } from '../../shared/utils/heatmapSymbologyHelpers';
import { UseMutationOptions } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { MutationKeys } from '../../enums/mutationKeys';
import { geoserverServiceMessages } from './geoserver.constants';
import { shapeKeyToMark } from '../../views/MapPortal/LayerProperties/Symbology/components/builtInShapes';
import { SingleSymbologyConfig } from '../../types/symbology.types';

const queryClient = new QueryClient();

// US-101662: signed S3 URLs contain `&` query separators that break SLD
// XML parsing — GeoServer responds 400 ("Invalid SLD") and the layer
// silently falls back to its default style. Anything we interpolate into an
// SLD attribute or text node MUST go through this first.
const escapeXml = (value: unknown): string => {
  if (value == null) return '';
  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
};

interface HeatmapConfig {
  layerType: string;
  radius: number;
  weightAttribute?: string;
  colorRamp: { id: string; colors: string[] };
  opacity: number;
  minZoom: number;
  maxZoom: number;
}

const GeoserverService = () => {
  const { fetchFeaturesKeyAndValueMapping } = MapPortalService();

  const generateHeatmapSLD = (layerName: string, config: HeatmapConfig): string => {
    const {
      radius,
      weightAttribute,
      colorRamp,
      opacity,
      // minZoom,
      // maxZoom,
    } = config;

    // const minScale = zoomToScaleDenominator(maxZoom);
    // const maxScale = zoomToScaleDenominator(minZoom);
    const colorMapEntries = generateColorMapEntries(colorRamp.colors);

    return `
    <StyledLayerDescriptor version="1.0.0"
        xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"
        xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
        xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">

    <NamedLayer>
        <Name>${layerName}</Name>
        <UserStyle>
        <Title>Heatmap Style</Title>

        <FeatureTypeStyle>

            <Transformation>
            <ogc:Function name="vec:Heatmap">

                <ogc:Function name="parameter">
                    <ogc:Literal>data</ogc:Literal>
                </ogc:Function>

                <ogc:Function name="parameter">
                    <ogc:Literal>radiusPixels</ogc:Literal>
                    <ogc:Function name="env">
                        <ogc:Literal>radius</ogc:Literal>
                        <ogc:Literal>${radius}</ogc:Literal>
                    </ogc:Function>
                </ogc:Function>

                <ogc:Function name="parameter">
                    <ogc:Literal>pixelsPerCell</ogc:Literal>
                    <ogc:Literal>${HEATMAP_PIXELS_PER_CELL}</ogc:Literal>
                </ogc:Function>

                ${
                  weightAttribute
                    ? `
                    <ogc:Function name="parameter">
                        <ogc:Literal>weightAttr</ogc:Literal>
                        <ogc:PropertyName>${weightAttribute}</ogc:PropertyName>
                    </ogc:Function>`
                    : ''
                }

                <ogc:Function name="parameter">
                    <ogc:Literal>outputBBOX</ogc:Literal>
                    <ogc:Function name="env">
                        <ogc:Literal>wms_bbox</ogc:Literal>
                    </ogc:Function>
                </ogc:Function>

                <ogc:Function name="parameter">
                    <ogc:Literal>outputWidth</ogc:Literal>
                    <ogc:Function name="env">
                        <ogc:Literal>wms_width</ogc:Literal>
                    </ogc:Function>
                </ogc:Function>

                <ogc:Function name="parameter">
                    <ogc:Literal>outputHeight</ogc:Literal>
                    <ogc:Function name="env">
                        <ogc:Literal>wms_height</ogc:Literal>
                    </ogc:Function>
                </ogc:Function>

            </ogc:Function>
            </Transformation>
            
            <Rule>
                <RasterSymbolizer>
                    <Geometry>
                        <ogc:PropertyName>the_geom</ogc:PropertyName>
                    </Geometry>

                    <Opacity>${(opacity / 100).toFixed(2)}</Opacity>

                    <ColorMap type="ramp">
                        ${colorMapEntries}
                    </ColorMap>
                </RasterSymbolizer>

            </Rule>
        </FeatureTypeStyle>
        </UserStyle>
    </NamedLayer>
    </StyledLayerDescriptor>`;
  };

  const applyHeatmapStyle = (): UseMutationOptions<string, Error, { layerName: string; config: HeatmapConfig }> => ({
    mutationKey: [MutationKeys.APPLY_HEATMAP_STYLE],
    mutationFn: async ({ layerName, config }) => {
      const sld = generateHeatmapSLD(layerName, config);
      const styleName = `heatmap_style_${layerName}`;

      const styleUrl = `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles/${styleName}`;
      const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
      const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
      const credentials = Base64.encode(`${username}:${password}`);

      const styleExists = await fetch(styleUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Basic ${credentials}`,
        },
      })
        .then((response) => response.ok)
        .catch(() => false);

      const method = styleExists ? 'PUT' : 'POST';
      const url = styleExists ? styleUrl : `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles?name=${styleName}`;

      await fetch(url, {
        method,
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/vnd.ogc.sld+xml',
        },
        body: sld,
      });

      await fetch(`${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/layers/mapaki:${layerName}`, {
        method: 'PUT',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'text/xml',
        },
        body: `<layer><defaultStyle><name>${styleName}</name></defaultStyle></layer>`,
      });

      return styleName;
    },
  });

  // GeoServer rejects non-numeric measures with:
  //   Invalid measure 'undefined', was expecting a number, eventually followed by px, m or ft
  // Coerce anything missing/NaN before it lands in <Size> or stroke-width.
  const sldMeasure = (value: unknown, fallback: number): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  // Fill = the line, Outline = an optional solid casing drawn beneath it, so the
  // casing's total width is the line width plus the outline weight on each side.
  // Colours may be literals or an ogc:Function; opacities are 0-1.
  const lineSymbolizers = (
    line: { color: string; opacity: number; type?: string | null; weight: unknown },
    outline: { color: string; opacity: number; type?: string | null; weight: unknown },
  ): string => {
    const lineWidth = Math.max(0.5, sldMeasure(line.weight, 2));
    const buffer = outline.type === 'none' ? 0 : Math.max(0, sldMeasure(outline.weight, 0));
    const lineType = line.type === 'dashed' || line.type === 'dotted' ? line.type : 'solid';
    const { dasharray, linecap } = strokeDashFor(lineType, lineWidth);
    const casing =
      buffer > 0
        ? `<LineSymbolizer><Stroke>
                        <CssParameter name="stroke">${outline.color}</CssParameter>
                        <CssParameter name="stroke-width">${lineWidth + buffer * 2}</CssParameter>
                        <CssParameter name="stroke-opacity">${outline.opacity}</CssParameter>
                    </Stroke></LineSymbolizer>`
        : '';
    return `${casing}<LineSymbolizer><Stroke>
                        <CssParameter name="stroke">${line.color}</CssParameter>
                        <CssParameter name="stroke-width">${lineWidth}</CssParameter>
                        <CssParameter name="stroke-opacity">${line.opacity}</CssParameter>
                        ${dasharray ? `<CssParameter name="stroke-dasharray">${dasharray}</CssParameter><CssParameter name="stroke-linecap">${linecap}</CssParameter>` : ''}
                    </Stroke></LineSymbolizer>`;
  };

  const generateQuantitySLD = (
    layerName: string,
    layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint',
    field: string,
    classBreaks: { min: number; max: number; color: string }[],
    iconSettings?: {
      icon: string;
      iconSize: number;
      iconRotation: number;
      iconType: 'recent' | 'styles' | 'upload';
      iconUrl: string | false;
    },
    fillOpacity?: number,
    outlineSettings?: {
      outlineColor: string;
      outlineWeight: number;
      outlineOpacity: number;
    },
  ) => {
    const rules = classBreaks
      .map((breakItem, index) => {
        const isLast = index === classBreaks.length - 1;

        const filter = `
        <ogc:Filter>
          <ogc:And>
            <ogc:PropertyIsGreaterThanOrEqualTo>
              <ogc:PropertyName>${field}</ogc:PropertyName>
              <ogc:Literal>${breakItem.min}</ogc:Literal>
            </ogc:PropertyIsGreaterThanOrEqualTo>
            <ogc:${isLast ? 'PropertyIsLessThanOrEqualTo' : 'PropertyIsLessThan'}>
              <ogc:PropertyName>${field}</ogc:PropertyName>
              <ogc:Literal>${breakItem.max}</ogc:Literal>
            </ogc:${isLast ? 'PropertyIsLessThanOrEqualTo' : 'PropertyIsLessThan'}>
          </ogc:And>
        </ogc:Filter>`;

        let symbolizer = '';

        const qtyOutlineWeight = sldMeasure(outlineSettings?.outlineWeight, 1);
        const qtyIconSize = sldMeasure(iconSettings?.iconSize, 10);
        const qtyIconRotation = sldMeasure(iconSettings?.iconRotation, 0);

        if (layerType === 'Point' || layerType === 'MultiPoint') {
          // Point symbolizer with icon
          if (iconSettings && iconSettings.iconType !== 'recent' && iconSettings.iconUrl) {
            symbolizer = `
                <PointSymbolizer>
                    <Graphic>
                    <ExternalGraphic>
                        <OnlineResource xlink:type="simple" xlink:href="${escapeXml(iconSettings.iconUrl)}" />
                        <Format>${escapeXml(getImageFormat(iconSettings.iconUrl))}</Format>
                    </ExternalGraphic>
                    <Size>${qtyIconSize}</Size>
                    <Rotation>${qtyIconRotation}</Rotation>
                    </Graphic>
                </PointSymbolizer>`;
          } else {
            const quantityMark = shapeKeyToMark(iconSettings?.icon);
            symbolizer = `
                <PointSymbolizer>
                    <Graphic>
                    <Mark>
                        <WellKnownName>${quantityMark.wellKnownName || 'circle'}</WellKnownName>
                        <Fill>
                            <CssParameter name="fill">${breakItem.color}</CssParameter>
                            <CssParameter name="fill-opacity">${fillOpacity}</CssParameter>
                        </Fill>
                        <Stroke>
                            <CssParameter name="stroke">${outlineSettings?.outlineColor}</CssParameter>
                            <CssParameter name="stroke-width">${qtyOutlineWeight}</CssParameter>
                            <CssParameter name="stroke-opacity">${outlineSettings?.outlineOpacity}</CssParameter>
                        </Stroke>
                    </Mark>
                    <Size>${qtyIconSize}</Size>
                    <Rotation>${qtyIconRotation + quantityMark.rotationOffset}</Rotation>
                    </Graphic>
                </PointSymbolizer>`;
          }
        } else if (layerType === 'MultiLineString') {
          symbolizer = `
                    <LineSymbolizer>
                        <Stroke>
                            <CssParameter name="stroke">${breakItem.color}</CssParameter>
                            <CssParameter name="stroke-width">${qtyOutlineWeight}</CssParameter>
                            <CssParameter name="stroke-opacity">${outlineSettings?.outlineOpacity}</CssParameter>
                        </Stroke>
                    </LineSymbolizer>`;
        } else {
          // Polygon symbolizer

          symbolizer = `
                    <PolygonSymbolizer>
                        <Fill>
                            <CssParameter name="fill">${breakItem.color}</CssParameter>
                            <CssParameter name="fill-opacity">${fillOpacity}</CssParameter>
                        </Fill>
                        <Stroke>
                            <CssParameter name="stroke">${outlineSettings?.outlineColor}</CssParameter>
                            <CssParameter name="stroke-width">${qtyOutlineWeight}</CssParameter>
                            <CssParameter name="stroke-opacity">${outlineSettings?.outlineOpacity}</CssParameter>
                        </Stroke>
                    </PolygonSymbolizer>`;
        }
        return `
        <Rule>
            ${filter}
            ${symbolizer}
        </Rule>`;
      })
      .join('');

    return `
        <StyledLayerDescriptor version="1.0.0"
        xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"
        xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
        xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <NamedLayer>
                <Name>${layerName}</Name>
                <UserStyle>
                    <FeatureTypeStyle>
                        ${rules}
                    </FeatureTypeStyle>
                </UserStyle>
            </NamedLayer>
        </StyledLayerDescriptor>`;
  };

  const applyQuantityStyle = (): UseMutationOptions<
    void,
    Error,
    {
      layerName: string;
      layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint';
      field: string;
      mode: 'jenks' | 'equalInterval' | 'quantile' | 'headsTails' | 'manual';
      numberOfBreaks: number;
      colorRamp: { id: string; colors: string[] };
      iconSettings?: {
        icon: string;
        iconSize: number;
        iconRotation: number;
        iconType: 'recent' | 'styles' | 'upload';
        iconUrl: string | false;
      };
      fillOpacity?: number;
      outlineSettings?: {
        outlineColor: string;
        outlineOpacity: number;
        outlineWeight: number;
      };
    }
  > => ({
    mutationKey: [MutationKeys.APPLY_QUANTITY_STYLE],
    mutationFn: async ({ layerName, layerType, field, mode, numberOfBreaks, colorRamp, iconSettings, fillOpacity, outlineSettings }) => {
      const { queryKey, queryFn } = fetchFeaturesKeyAndValueMapping(layerName);
      if (typeof queryFn !== 'function') {
        throw new Error('Query function not available for feature mapping.');
      }
      const features = await queryClient.fetchQuery({ queryKey, queryFn });
      const data = features[field] || [];

      const breaks = calculateClassBreaks(data, mode, numberOfBreaks);

      const pickedColors = pickColorsForBreaks(colorRamp.colors, numberOfBreaks);

      const classBreaksWithColors = breaks.map((breakItem, index) => ({
        ...breakItem,
        color: pickedColors[index],
      }));

      const sld = generateQuantitySLD(layerName, layerType, field, classBreaksWithColors, iconSettings, fillOpacity, outlineSettings);

      const styleName = `quantity_style_${layerName}`;
      const styleUrl = `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles/${styleName}`;
      const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
      const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
      const credentials = Base64.encode(`${username}:${password}`);

      const styleExists = await fetch(styleUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Basic ${credentials}`,
        },
      })
        .then((response) => response.ok)
        .catch(() => false);

      const method = styleExists ? 'PUT' : 'POST';
      const url = styleExists ? styleUrl : `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles?name=${styleName}`;

      await fetch(url, {
        method,
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/vnd.ogc.sld+xml',
        },
        body: sld,
      });

      await fetch(`${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/layers/mapaki:${layerName}`, {
        method: 'PUT',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'text/xml',
        },
        body: `<layer><defaultStyle><name>${styleName}</name></defaultStyle></layer>`,
      });
    },
  });

  const generateDynamicSLD = (
    layerName: string,
    layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint',
    symbology: string,
    fillColor: string,
    strokeColor: string,
    fillOpacity: number,
    strokeWidth: number,
    strokeOpacity: number,
    strokeType: 'solid' | 'none' | 'dashed' | 'dotted' = 'solid',
    key: string,
    value: string,
    uniqueFill: string,
    uniqueFillOpacity: number,
    uniqueOutline: string,
    uniqueOutlineType: 'solid' | 'none' | 'dashed' | 'dotted' = 'solid',
    uniqueOutlineOpacity: number,
    uniqueOutlineWeight: number,
    selectedIcon: string,
    iconSize: number,
    iconRotation: number,
    iconType: 'recent' | 'styles' | 'upload',
    iconUrl: string | false,
    uniqueIcon: string,
    uniqueIconSize: number,
    uniqueIconRotation: number,
    uniqueIconType: 'recent' | 'styles' | 'upload',
    uniqueIconUrl?: string | false,
    fillType: 'solid' | 'none' | 'dashed' | 'dotted' = 'solid',
    lineWeight: number = 2,
  ) => {
    // Numeric SLD measures — never emit the literal string "undefined".
    const safeStrokeWidth = sldMeasure(strokeWidth, 1);
    const safeUniqueOutlineWeight = sldMeasure(uniqueOutlineWeight, 1);
    const safeIconSize = sldMeasure(iconSize, 12);
    const safeIconRotation = sldMeasure(iconRotation, 0);
    const safeUniqueIconSize = sldMeasure(uniqueIconSize, 12);
    const safeUniqueIconRotation = sldMeasure(uniqueIconRotation, 0);

    // Dash pattern is scaled to the stroke width (and dotted uses round caps),
    // so thick dotted/dashed outlines render as clean dots/dashes instead of
    // stretched blobs. A "none" outline type emits no <Stroke> at all — the
    // empty-string sentinel below is the "no stroke" marker the blocks check.
    const noStroke = strokeType === 'none';
    const { dasharray: strokeDasharray, linecap: strokeLinecap } = strokeDashFor(strokeType, safeStrokeWidth);

    const noUniqueStroke = uniqueOutlineType === 'none';
    const { dasharray: uniqueStrokeDasharray, linecap: uniqueStrokeLinecap } = strokeDashFor(uniqueOutlineType, safeUniqueOutlineWeight);

    // Fill type "none" must render the geometry unfilled (distinct from solid).
    const noFill = fillType === 'none';

    // Shared <CssParameter> fragment for dash + linecap, included only when the
    // outline is actually dashed/dotted.
    const dashCss = (dash: string, cap: string) =>
      dash ? `<CssParameter name="stroke-dasharray">${dash}</CssParameter><CssParameter name="stroke-linecap">${cap}</CssParameter>` : '';

    // US-101662: built-in shapes are stored as "shape:<name>" in the
    // frontend; translate to the bare SLD WellKnownName GeoServer expects
    // (with any rotation offset, e.g. diamond = square turned 45°) before
    // emitting the Mark. Image keys yield offset 0, so the rotation is
    // safe to apply to the ExternalGraphic branch too.
    const singleMark = shapeKeyToMark(selectedIcon);
    const pointSymbolizer = `
            <PointSymbolizer>
                <Graphic>
                    ${
                      iconType === 'styles' || iconType === 'upload'
                        ? `
                        <ExternalGraphic>
                            <OnlineResource xlink:type="simple" xlink:href="${escapeXml(iconUrl)}" />
                            <Format>${escapeXml(getImageFormat(iconUrl))}</Format>
                        </ExternalGraphic>
                        `
                        : `
                        <Mark>
                            <WellKnownName>${escapeXml(singleMark.wellKnownName || 'circle')}</WellKnownName>
                            ${
                              noFill
                                ? ''
                                : `<Fill>
                                <CssParameter name="fill">${fillColor}</CssParameter>
                                <CssParameter name="fill-opacity">${fillOpacity}</CssParameter>
                            </Fill>`
                            }
                            ${
                              noStroke
                                ? ''
                                : `<Stroke>
                                <CssParameter name="stroke">${strokeColor}</CssParameter>
                                <CssParameter name="stroke-width">${safeStrokeWidth}</CssParameter>
                                <CssParameter name="stroke-opacity">${strokeOpacity}</CssParameter>
                                ${dashCss(strokeDasharray, strokeLinecap)}
                            </Stroke>`
                            }
                        </Mark>`
                    }
                    <Size>${safeIconSize}</Size>
                    <Rotation>${safeIconRotation + (iconType === 'styles' || iconType === 'upload' ? 0 : singleMark.rotationOffset)}</Rotation>
                </Graphic>
            </PointSymbolizer>`;

    const uniquePointSymbolizer =
      key && value
        ? `
            <Rule>
                <ogc:Filter>
                <ogc:PropertyIsEqualTo>
                    <ogc:PropertyName>${key}</ogc:PropertyName>
                    <ogc:Literal>${value}</ogc:Literal>
                </ogc:PropertyIsEqualTo>
                </ogc:Filter>
                <PointSymbolizer>
                <Graphic>
                    ${
                      uniqueIconType === 'styles' || uniqueIconType === 'upload'
                        ? `
                    <ExternalGraphic>
                        <OnlineResource xlink:type="simple" xlink:href="${escapeXml(uniqueIconUrl)}" />
                        <Format>${escapeXml(getImageFormat(uniqueIconUrl))}</Format>
                    </ExternalGraphic>`
                        : `
                    <Mark>
                        <WellKnownName>${escapeXml(shapeKeyToMark(uniqueIcon).wellKnownName || 'circle')}</WellKnownName>
                        <Fill>
                            <CssParameter name="fill">${uniqueFill}</CssParameter>
                            <CssParameter name="fill-opacity">${uniqueFillOpacity}</CssParameter>
                        </Fill>
                    </Mark>
                    `
                    }
                    <Size>${safeUniqueIconSize}</Size>
                    <Rotation>${safeUniqueIconRotation + (uniqueIconType === 'styles' || uniqueIconType === 'upload' ? 0 : shapeKeyToMark(uniqueIcon).rotationOffset)}</Rotation>
                </Graphic>
                </PointSymbolizer>
            </Rule>`
        : '';

    const polygonLineBlock = `
            <PolygonSymbolizer>
                ${
                  noFill
                    ? ''
                    : `<Fill>
                    <CssParameter name="fill">${fillColor}</CssParameter>
                    <CssParameter name="fill-opacity">${fillOpacity}</CssParameter>
                </Fill>`
                }
                ${
                  noStroke
                    ? ''
                    : `
                <Stroke>
                    <CssParameter name="stroke">${strokeColor}</CssParameter>
                    <CssParameter name="stroke-width">${safeStrokeWidth}</CssParameter>
                    <CssParameter name="stroke-opacity">${strokeOpacity}</CssParameter>
                    ${dashCss(strokeDasharray, strokeLinecap)}
                 </Stroke>`
                }
            </PolygonSymbolizer>
        `;

    const uniquePolygonBlock =
      key && value
        ? `<Rule>
                <ogc:Filter>
                  <ogc:PropertyIsEqualTo>
                    <ogc:PropertyName>${key}</ogc:PropertyName>
                    <ogc:Literal>${value}</ogc:Literal>
                  </ogc:PropertyIsEqualTo>
                </ogc:Filter>
                <PolygonSymbolizer>
                    <Fill>
                        <CssParameter name="fill">${uniqueFill}</CssParameter>
                        <CssParameter name="fill-opacity">${uniqueFillOpacity}</CssParameter>
                    </Fill>
                    ${
                      noUniqueStroke
                        ? ''
                        : `
                    <Stroke>
                        <CssParameter name="stroke">${uniqueOutline}</CssParameter>
                        <CssParameter name="stroke-width">${safeUniqueOutlineWeight}</CssParameter>
                        <CssParameter name="stroke-opacity">${uniqueOutlineOpacity}</CssParameter>
                        ${dashCss(uniqueStrokeDasharray, uniqueStrokeLinecap)}
                    </Stroke>
                        `
                    }
                </PolygonSymbolizer>
            </Rule>`
        : '';

    // Lines: Fill is the line itself (colour / opacity / solid-dashed-dotted / weight);
    // Outline is an optional solid buffer around it.
    const lineSymbolizerBlock = lineSymbolizers(
      { color: fillColor, opacity: fillOpacity, type: fillType, weight: lineWeight },
      { color: strokeColor, opacity: strokeOpacity, type: strokeType, weight: strokeWidth },
    );

    const uniqueLineBlock =
      key && value
        ? `
                    <Rule>
                        <ogc:Filter>
                            <ogc:PropertyIsEqualTo>
                                <ogc:PropertyName>${key}</ogc:PropertyName>
                                <ogc:Literal>${value}</ogc:Literal>
                            </ogc:PropertyIsEqualTo>
                        </ogc:Filter>
                        ${lineSymbolizers(
                          { color: uniqueFill, opacity: uniqueFillOpacity, type: fillType, weight: lineWeight },
                          { color: uniqueOutline, opacity: uniqueOutlineOpacity, type: uniqueOutlineType, weight: uniqueOutlineWeight },
                        )}
                    </Rule>`
        : '';

    return `
        <StyledLayerDescriptor version="1.0.0"
        xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"
        xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
        xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <NamedLayer>
                <Name>${layerName}</Name>
                <UserStyle>
                    <FeatureTypeStyle>
                        <!-- SINGLE RULE -->
                        <Rule>
                            ${symbology === 'unique' ? '<ElseFilter/>' : ''}
                            ${
                              symbology === 'single'
                                ? layerType === 'Point' || layerType === 'MultiPoint'
                                  ? pointSymbolizer
                                  : layerType === 'MultiLineString'
                                    ? lineSymbolizerBlock
                                    : polygonLineBlock
                                : ''
                            }
                        </Rule>

                        <!-- UNIQUE RULE -->
                        ${
                          symbology === 'unique'
                            ? layerType === 'Point' || layerType === 'MultiPoint'
                              ? uniquePointSymbolizer
                              : layerType === 'MultiLineString'
                                ? uniqueLineBlock
                                : uniquePolygonBlock
                            : ''
                        }
                    </FeatureTypeStyle>
                </UserStyle>
            </NamedLayer>
        </StyledLayerDescriptor>`;
  };

  const applyDynamicStyle = (): UseMutationOptions<
    void,
    Error,
    {
      layerName: string;
      layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint';
      symbology: string;
      fillColor: string;
      strokeColor: string;
      fillOpacity?: number;
      fillType?: 'solid' | 'none' | 'dashed' | 'dotted';
      lineWeight?: number;
      strokeWidth?: number;
      strokeOpacity: number;
      outlineType?: 'solid' | 'none' | 'dashed' | 'dotted';
      key: string;
      value: string;
      uniqueFill: string;
      uniqueFillOpacity: number;
      uniqueOutline: string;
      uniqueOutlineType?: 'solid' | 'none' | 'dashed' | 'dotted';
      uniqueOutlineOpacity: number;
      uniqueOutlineWeight: number;
      selectedIcon: string;
      iconSize: number;
      iconRotation: number;
      iconType: 'recent' | 'styles' | 'upload';
      iconUrl: string | false;
      uniqueIcon: string;
      uniqueIconSize: number;
      uniqueIconRotation: number;
      uniqueIconType: 'recent' | 'styles' | 'upload';
      uniqueIconUrl?: string | false;
    }
  > => ({
    mutationKey: [MutationKeys.APPLY_DYNAMIC_STYLE],
    mutationFn: async ({
      layerName,
      layerType,
      symbology,
      fillColor,
      strokeColor,
      fillOpacity = 1,
      fillType = 'solid',
      lineWeight = 2,
      strokeWidth = 0,
      strokeOpacity,
      outlineType = 'solid',
      key,
      value,
      uniqueFill,
      uniqueFillOpacity,
      uniqueOutline,
      uniqueOutlineType = 'solid',
      uniqueOutlineOpacity,
      uniqueOutlineWeight,
      selectedIcon,
      iconSize,
      iconRotation,
      iconType,
      iconUrl,
      uniqueIcon,
      uniqueIconSize,
      uniqueIconRotation,
      uniqueIconType,
      uniqueIconUrl,
    }) => {
      const sld = generateDynamicSLD(
        layerName,
        layerType,
        symbology,
        fillColor,
        strokeColor,
        fillOpacity,
        strokeWidth,
        strokeOpacity,
        outlineType,
        key,
        value,
        uniqueFill,
        uniqueFillOpacity,
        uniqueOutline,
        uniqueOutlineType,
        uniqueOutlineOpacity,
        uniqueOutlineWeight,
        selectedIcon,
        iconSize,
        iconRotation,
        iconType,
        iconUrl,
        uniqueIcon,
        uniqueIconSize,
        uniqueIconRotation,
        uniqueIconType,
        uniqueIconUrl,
        fillType,
        lineWeight,
      );
      const styleName = `dynamic_style_${layerName}`;

      const styleUrl = `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles/${styleName}`;
      const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
      const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
      const credentials = Base64.encode(`${username}:${password}`);

      const styleExists = await fetch(styleUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Basic ${credentials}`,
        },
      })
        .then((response) => response.ok)
        .catch(() => false);

      const method = styleExists ? 'PUT' : 'POST';

      const url = styleExists ? styleUrl : `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles?name=${styleName}`;

      await fetch(url, {
        method,
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/vnd.ogc.sld+xml',
        },
        body: sld,
      });

      await fetch(`${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/layers/mapaki:${layerName}`, {
        method: 'PUT',
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'text/xml',
        },
        body: `<layer><defaultStyle><name>${styleName}</name></defaultStyle></layer>`,
      });
    },
  });

  // ===================================================================
  // UNIQUE (categorized) symbology — one SLD <Rule> per category.
  // Features whose value matches no category get no rule and are therefore
  // not drawn (US-101664 Scenarios 5/6/12: removed categories disappear).
  // ===================================================================
  // Dash pattern scaled to the stroke width so dotted/dashed look right at any
  // weight. Dotted uses a 0-length dash + round linecap → clean round dots
  // (a fixed "1 5" pattern turns into ugly blobs at large widths).
  const strokeDashFor = (type?: string | null, width?: number): { dasharray: string; linecap: string } => {
    const w = Math.max(1, Number(width) || 1);
    if (type === 'dashed') return { dasharray: `${(w * 2).toFixed(1)} ${(w * 1.3).toFixed(1)}`, linecap: 'butt' };
    if (type === 'dotted') return { dasharray: `0 ${(w * 1.8).toFixed(1)}`, linecap: 'round' };
    return { dasharray: '', linecap: 'butt' };
  };

  // Stroke <CssParameter>s — empty string when the outline type is "none".
  const strokeParams = (color: string, width: number, opacity: number, type?: string | null): string => {
    if (type === 'none') return '';
    const safeWidth = sldMeasure(width, 1);
    const { dasharray, linecap } = strokeDashFor(type, safeWidth);
    return `
                    <CssParameter name="stroke">${color}</CssParameter>
                    <CssParameter name="stroke-width">${safeWidth}</CssParameter>
                    <CssParameter name="stroke-opacity">${(opacity / 100).toFixed(2)}</CssParameter>
                    ${dasharray ? `<CssParameter name="stroke-dasharray">${dasharray}</CssParameter><CssParameter name="stroke-linecap">${linecap}</CssParameter>` : ''}`;
  };

  // Fill <CssParameter>s — empty string when the fill type is "none" so the
  // geometry renders unfilled (Fill None ≠ Fill Solid).
  const fillParams = (color: string, opacity: number, fillType?: string | null): string => {
    if (fillType === 'none') return '';
    return `
                    <CssParameter name="fill">${color}</CssParameter>
                    <CssParameter name="fill-opacity">${(opacity / 100).toFixed(2)}</CssParameter>`;
  };

  const buildPointSymbolizer = (sym: SingleSymbologyConfig, iconUrl: string | false) => {
    const size = sldMeasure(sym.icon?.size, 10);
    const rotation = sldMeasure(sym.icon?.rotation, 0);
    if (iconUrl) {
      return `
                <PointSymbolizer><Graphic>
                    <ExternalGraphic>
                        <OnlineResource xlink:type="simple" xlink:href="${escapeXml(iconUrl)}" />
                        <Format>${escapeXml(getImageFormat(iconUrl))}</Format>
                    </ExternalGraphic>
                    <Size>${size}</Size>
                    <Rotation>${rotation}</Rotation>
                </Graphic></PointSymbolizer>`;
    }
    const mark = shapeKeyToMark(sym.icon?.key);
    const fill = fillParams(sym.fill, sym.fillOpacity, sym.fillType);
    const stroke = strokeParams(sym.outline, sym.outlineWeight, sym.outlineOpacity, sym.outlineType);
    return `
            <PointSymbolizer><Graphic><Mark>
                <WellKnownName>${escapeXml(mark.wellKnownName || 'circle')}</WellKnownName>
                ${fill ? `<Fill>${fill}</Fill>` : ''}
                ${stroke ? `<Stroke>${stroke}</Stroke>` : ''}
            </Mark>
            <Size>${size}</Size>
            <Rotation>${rotation + mark.rotationOffset}</Rotation>
            </Graphic></PointSymbolizer>`;
  };

  const buildPolygonSymbolizer = (sym: SingleSymbologyConfig) => {
    const fill = fillParams(sym.fill, sym.fillOpacity, sym.fillType);
    const stroke = strokeParams(sym.outline, sym.outlineWeight, sym.outlineOpacity, sym.outlineType);
    return `
            <PolygonSymbolizer>
                ${fill ? `<Fill>${fill}</Fill>` : ''}
                ${stroke ? `<Stroke>${stroke}</Stroke>` : ''}
            </PolygonSymbolizer>`;
  };

  const buildLineSymbolizer = (sym: SingleSymbologyConfig) =>
    lineSymbolizers(
      { color: sym.fill, opacity: sym.fillOpacity / 100, type: sym.fillType, weight: sym.lineWeight },
      { color: sym.outline, opacity: sym.outlineOpacity / 100, type: sym.outlineType, weight: sym.outlineWeight },
    );

  // Are all categories identical except for fill/outline COLOR? (No per-category
  // icons, uniform stroke/fill style + point mark.) Only then can a single
  // Recode rule reproduce them exactly.
  const uniqueRecodeEligible = (categories: { symbology: SingleSymbologyConfig; iconUrl: string | false }[]): boolean => {
    if (categories.length === 0) return false;
    if (categories.some((c) => c.iconUrl)) return false; // ExternalGraphic can't be Recode'd
    const base = categories[0].symbology;
    const same = (pick: (s: SingleSymbologyConfig) => unknown) => categories.every((c) => pick(c.symbology) === pick(base));
    return (
      same((s) => s.fillType) &&
      same((s) => s.fillOpacity) &&
      same((s) => s.outlineType) &&
      same((s) => s.outlineWeight) &&
      same((s) => s.lineWeight) &&
      same((s) => s.outlineOpacity) &&
      same((s) => s.icon?.key) &&
      same((s) => s.icon?.size) &&
      same((s) => s.icon?.rotation)
    );
  };

  // PERF: a single rule whose fill/stroke colors are computed by an ogc:Function
  // Recode (value→color) renders in O(1) per feature, versus GeoServer scanning
  // N PropertyIsEqualTo rules. For 500 categories this turns a ~250KB / O(500)
  // SLD into a ~15KB / O(1) one. Unmatched values return null from Recode → the
  // feature paints nothing, exactly matching the per-rule "no rule → not drawn"
  // behavior (so a removed category still hides its features).
  const buildUniqueRecodeRule = (
    layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint',
    field: string,
    categories: { value: string | number; symbology: SingleSymbologyConfig; iconUrl: string | false }[],
  ): string => {
    const base = categories[0].symbology;
    const recode = (pick: (s: SingleSymbologyConfig) => string) =>
      `<ogc:Function name="Recode"><ogc:PropertyName>${escapeXml(field)}</ogc:PropertyName>${categories
        .map((c) => `<ogc:Literal>${escapeXml(c.value)}</ogc:Literal><ogc:Literal>${escapeXml(pick(c.symbology))}</ogc:Literal>`)
        .join('')}</ogc:Function>`;

    const fillBlock =
      base.fillType === 'none'
        ? ''
        : `<Fill><CssParameter name="fill">${recode((s) => s.fill)}</CssParameter><CssParameter name="fill-opacity">${(base.fillOpacity / 100).toFixed(2)}</CssParameter></Fill>`;

    const strokeBlock = (() => {
      if (base.outlineType === 'none') return '';
      const safeOutlineWeight = sldMeasure(base.outlineWeight, 1);
      const { dasharray, linecap } = strokeDashFor(base.outlineType, safeOutlineWeight);
      return `<Stroke><CssParameter name="stroke">${recode((s) => s.outline)}</CssParameter><CssParameter name="stroke-width">${safeOutlineWeight}</CssParameter><CssParameter name="stroke-opacity">${(base.outlineOpacity / 100).toFixed(2)}</CssParameter>${dasharray ? `<CssParameter name="stroke-dasharray">${dasharray}</CssParameter><CssParameter name="stroke-linecap">${linecap}</CssParameter>` : ''}</Stroke>`;
    })();

    let symbolizer = '';
    if (layerType === 'Point' || layerType === 'MultiPoint') {
      const mark = shapeKeyToMark(base.icon?.key);
      const size = sldMeasure(base.icon?.size, 10);
      const rotation = sldMeasure(base.icon?.rotation, 0);
      symbolizer = `<PointSymbolizer><Graphic><Mark><WellKnownName>${escapeXml(
        mark.wellKnownName || 'circle',
      )}</WellKnownName>${fillBlock}${strokeBlock}</Mark><Size>${size}</Size><Rotation>${rotation + mark.rotationOffset}</Rotation></Graphic></PointSymbolizer>`;
    } else if (layerType === 'MultiLineString') {
      symbolizer = lineSymbolizers(
        { color: recode((s) => s.fill), opacity: base.fillOpacity / 100, type: base.fillType, weight: base.lineWeight },
        { color: recode((s) => s.outline), opacity: base.outlineOpacity / 100, type: base.outlineType, weight: base.outlineWeight },
      );
    } else {
      symbolizer = `<PolygonSymbolizer>${fillBlock}${strokeBlock}</PolygonSymbolizer>`;
    }
    return `<Rule>${symbolizer}</Rule>`; // single rule, no per-category filter
  };

  const generateUniqueSLD = (
    layerName: string,
    layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint',
    field: string,
    categories: { value: string | number; symbology: SingleSymbologyConfig; iconUrl: string | false }[],
  ) => {
    // US-101664 Scenario 6: with no categories the layer must render
    // nothing. Emit a single always-false rule so the SLD stays valid
    // (GeoServer rejects a FeatureTypeStyle with zero rules).
    const emptyRule = `<Rule><ogc:Filter><ogc:PropertyIsEqualTo><ogc:Literal>1</ogc:Literal><ogc:Literal>0</ogc:Literal></ogc:PropertyIsEqualTo></ogc:Filter></Rule>`;
    let rules: string;
    if (categories.length === 0) {
      rules = emptyRule;
    } else if (uniqueRecodeEligible(categories)) {
      // Fast path: equivalent to the per-rule SLD but O(1) per feature.
      rules = buildUniqueRecodeRule(layerType, field, categories);
    } else {
      // Fallback (per-category icons / heterogeneous styling): one rule each.
      rules = categories
        .map((cat) => {
          const filter = `
                <ogc:Filter>
                    <ogc:PropertyIsEqualTo>
                        <ogc:PropertyName>${escapeXml(field)}</ogc:PropertyName>
                        <ogc:Literal>${escapeXml(cat.value)}</ogc:Literal>
                    </ogc:PropertyIsEqualTo>
                </ogc:Filter>`;
          let symbolizer = '';
          if (layerType === 'Point' || layerType === 'MultiPoint') symbolizer = buildPointSymbolizer(cat.symbology, cat.iconUrl);
          else if (layerType === 'MultiLineString') symbolizer = buildLineSymbolizer(cat.symbology);
          else symbolizer = buildPolygonSymbolizer(cat.symbology);
          return `<Rule>${filter}${symbolizer}</Rule>`;
        })
        .join('');
    }

    return `
        <StyledLayerDescriptor version="1.0.0"
        xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"
        xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
        xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <NamedLayer>
                <Name>${layerName}</Name>
                <UserStyle>
                    <FeatureTypeStyle>
                        ${rules}
                    </FeatureTypeStyle>
                </UserStyle>
            </NamedLayer>
        </StyledLayerDescriptor>`;
  };

  const applyUniqueStyle = (): UseMutationOptions<
    void,
    Error,
    {
      layerName: string;
      layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint';
      field: string;
      categories: { value: string | number; symbology: SingleSymbologyConfig; iconUrl: string | false }[];
    }
  > => ({
    mutationKey: [MutationKeys.APPLY_UNIQUE_STYLE],
    mutationFn: async ({ layerName, layerType, field, categories }) => {
      const sld = generateUniqueSLD(layerName, layerType, field, categories);
      // Reuse the same style name as the single path so the layer's
      // defaultStyle stays stable when toggling single <-> unique.
      const styleName = `dynamic_style_${layerName}`;
      const styleUrl = `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles/${styleName}`;
      const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
      const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
      const credentials = Base64.encode(`${username}:${password}`);

      const styleExists = await fetch(styleUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Basic ${credentials}` },
      })
        .then((response) => response.ok)
        .catch(() => false);

      const method = styleExists ? 'PUT' : 'POST';
      const url = styleExists ? styleUrl : `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles?name=${styleName}`;

      await fetch(url, {
        method,
        headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'application/vnd.ogc.sld+xml' },
        body: sld,
      });

      await fetch(`${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/layers/mapaki:${layerName}`, {
        method: 'PUT',
        headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'text/xml' },
        body: `<layer><defaultStyle><name>${styleName}</name></defaultStyle></layer>`,
      });
    },
  });

  // ===================================================================
  // QUANTITY (graduated) symbology — one rule per class break, driven by
  // the typed QuantitySymbologyConfig.classes (per-class symbology + range).
  // ===================================================================
  interface QuantityRenderClass {
    min: number;
    max: number;
    minOp: '>' | '>=' | null;
    maxOp: '<' | '<=' | null;
    symbology: SingleSymbologyConfig;
    iconUrl: string | false;
  }

  const buildRangeFilter = (field: string, cls: QuantityRenderClass): string => {
    const conds: string[] = [];
    if (cls.minOp) {
      const op = cls.minOp === '>=' ? 'PropertyIsGreaterThanOrEqualTo' : 'PropertyIsGreaterThan';
      conds.push(`<ogc:${op}><ogc:PropertyName>${escapeXml(field)}</ogc:PropertyName><ogc:Literal>${cls.min}</ogc:Literal></ogc:${op}>`);
    }
    if (cls.maxOp) {
      const op = cls.maxOp === '<=' ? 'PropertyIsLessThanOrEqualTo' : 'PropertyIsLessThan';
      conds.push(`<ogc:${op}><ogc:PropertyName>${escapeXml(field)}</ogc:PropertyName><ogc:Literal>${cls.max}</ogc:Literal></ogc:${op}>`);
    }
    if (conds.length === 0) return '';
    const inner = conds.length === 2 ? `<ogc:And>${conds.join('')}</ogc:And>` : conds[0];
    return `<ogc:Filter>${inner}</ogc:Filter>`;
  };

  const generateQuantityClassSLD = (
    layerName: string,
    layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint',
    field: string,
    classes: QuantityRenderClass[],
  ) => {
    const rules = classes
      .filter((c) => c.minOp || c.maxOp) // skip unconfigured (blank Manual) classes
      .map((c) => {
        const filter = buildRangeFilter(field, c);
        let symbolizer = '';
        if (layerType === 'Point' || layerType === 'MultiPoint') symbolizer = buildPointSymbolizer(c.symbology, c.iconUrl);
        else if (layerType === 'MultiLineString') symbolizer = buildLineSymbolizer(c.symbology);
        else symbolizer = buildPolygonSymbolizer(c.symbology);
        return `<Rule>${filter}${symbolizer}</Rule>`;
      })
      .join('');
    // Matches-nothing fallback keeps the SLD valid when no class is configured.
    const emptyRule = `<Rule><ogc:Filter><ogc:PropertyIsEqualTo><ogc:Literal>1</ogc:Literal><ogc:Literal>0</ogc:Literal></ogc:PropertyIsEqualTo></ogc:Filter></Rule>`;
    return `
        <StyledLayerDescriptor version="1.0.0"
        xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"
        xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc"
        xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
            <NamedLayer>
                <Name>${layerName}</Name>
                <UserStyle>
                    <FeatureTypeStyle>
                        ${rules || emptyRule}
                    </FeatureTypeStyle>
                </UserStyle>
            </NamedLayer>
        </StyledLayerDescriptor>`;
  };

  const applyQuantityClassStyle = (): UseMutationOptions<
    void,
    Error,
    {
      layerName: string;
      layerType: 'Point' | 'Polygon' | 'MultiLineString' | 'MultiPoint';
      field: string;
      classes: QuantityRenderClass[];
    }
  > => ({
    mutationKey: [MutationKeys.APPLY_QUANTITY_STYLE],
    mutationFn: async ({ layerName, layerType, field, classes }) => {
      const sld = generateQuantityClassSLD(layerName, layerType, field, classes);
      const styleName = `quantity_style_${layerName}`;
      const styleUrl = `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles/${styleName}`;
      const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
      const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
      const credentials = Base64.encode(`${username}:${password}`);

      const styleExists = await fetch(styleUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Basic ${credentials}` },
      })
        .then((response) => response.ok)
        .catch(() => false);

      const method = styleExists ? 'PUT' : 'POST';
      const url = styleExists ? styleUrl : `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/styles?name=${styleName}`;

      await fetch(url, {
        method,
        headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'application/vnd.ogc.sld+xml' },
        body: sld,
      });

      await fetch(`${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/rest/layers/mapaki:${layerName}`, {
        method: 'PUT',
        headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'text/xml' },
        body: `<layer><defaultStyle><name>${styleName}</name></defaultStyle></layer>`,
      });
    },
  });

  const generateWFSTransaction = (layerName: any, featureGeoJSON: any) => {
    const coordinates = featureGeoJSON.geometry.coordinates[0].map((coord: any) => coord.join(' ')).join(', ');

    return `
        <wfs:Transaction service="WFS" version="1.0.0"
          xmlns:wfs="http://www.opengis.net/wfs"
          xmlns:gml="http://www.opengis.net/gml"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
          xmlns:ogc="http://www.opengis.net/ogc"
          xmlns:geoserver="http://www.geoserver.org"
          xsi:schemaLocation="http://www.opengis.net/wfs
            http://schemas.opengis.net/wfs/1.0.0/WFS-transaction.xsd">
          <wfs:Insert>
            <feature:${layerName} xmlns:feature="http://www.geoserver.org">
              <feature:geometry>
                <gml:Polygon srsName="EPSG:4326">
                  <gml:outerBoundaryIs>
                    <gml:LinearRing>
                      <gml:coordinates>
                        ${coordinates}
                      </gml:coordinates>
                    </gml:LinearRing>
                  </gml:outerBoundaryIs>
                </gml:Polygon>
              </feature:geometry>
            </feature:${layerName}>
          </wfs:Insert>
        </wfs:Transaction>`;
  };

  const saveFeatureToGeoServer = (): UseMutationOptions<void, Error, { layerName: any; featureGeoJSON: any }> => ({
    mutationKey: [MutationKeys.SAVE_FEATURE_TO_GEOSERVER],
    mutationFn: async ({ layerName, featureGeoJSON }) => {
      const wfsTransactionXML = generateWFSTransaction(layerName, featureGeoJSON);

      const WFS_URL = `${ApiRoutes.GEOSERVER_BASE_URL}/geoserver/wfs`;
      const username = import.meta.env.VITE_APP_CESIUM_USERNAME as string;
      const password = import.meta.env.VITE_APP_CESIUM_PASSWORD as string;
      const credentials = Base64.encode(`${username}:${password}`);

      const response = await fetch(WFS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml',
          Authorization: `Basic ${credentials}`,
        },
        body: wfsTransactionXML,
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }
    },
  });

  const createWMSLayer = (): UseMutationOptions<void, Error, { layerName: string; capabilitiesURL: string; mapId: string; folderId: string }> => ({
    mutationKey: [MutationKeys.CREATE_WMS_LAYER],
    mutationFn: async ({ layerName, capabilitiesURL, mapId, folderId }) => {
      const payload = {
        wms: {
          name: layerName,
          capabilitiesURL,
          type: 'WMS',
          folderId,
        },
      };
      const url = generatePath(ApiRoutes.CREATE_WMS, { mapId });
      await axiosInstance.post(url, payload);
    },
    onSuccess: () => {
      Notification({
        message: geoserverServiceMessages.CREATE_WMS_LAYER.SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error: any) => {
      Notification({
        message: geoserverServiceMessages.CREATE_WMS_LAYER.ERROR,
        description: error?.response?.data?.message || error?.message || geoserverServiceMessages.FALLBACK_ERROR,
        type: NotificationTypes.ERROR,
      });
    },
  });

  return {
    applyDynamicStyle,
    applyUniqueStyle,
    generateWFSTransaction,
    saveFeatureToGeoServer,
    createWMSLayer,
    applyQuantityStyle,
    applyQuantityClassStyle,
    applyHeatmapStyle,
  };
};

export default GeoserverService;
